import { UniversalTrackerBlueprintMin, UtrValueType, TableColumnType } from '@g17eco/types/universalTracker';
import { canAddTarget, isNumericTableColumnType, isSingleRowTableType } from '@utils/universalTracker';

// types that doesn't include value list, table.
export const SIMPLE_TYPES: string[] = [
  UtrValueType.Number,
  UtrValueType.Percentage,
  UtrValueType.Date,
  UtrValueType.Text,
  UtrValueType.Sample,
];

export enum QuestionFilterType {
  Pack = 'pack',
  Subpack = 'subPack',
  Question = 'question',
  Input = 'input',
}

export type ValidatingFunc = (
  question: UniversalTrackerBlueprintMin,
  valueListCode?: string,
) => {
  isAllowed: boolean;
  disableReason?: string;
};

// Still show disallowed option but disable it with reason.
export const checkIsNumericInput: ValidatingFunc = (question, valueListCode) => {
  if (!valueListCode) {
    const isAllowed = canAddTarget(question);
    return { isAllowed: true, disableReason: isAllowed ? undefined : 'Not supported' };
  }

  const column = question.valueValidation?.table?.columns?.find((c) => c.code === valueListCode);
  if (!column) {
    return { isAllowed: false };
  }

  const isAllowed = isNumericTableColumnType(column.type);
  return { isAllowed: true, disableReason: isAllowed ? undefined : 'Not supported' };
};

/**
 * Validates whether a question/column is selectable for text widgets.
 *
 * Called in two stages during the hierarchical filter selection:
 * 1. Question level (no valueListCode) — determines if the question appears in the dropdown.
 *    Single-row tables return allowed here to let the user drill into column selection.
 * 2. Column level (with valueListCode) — validates the specific column type,
 *    only allowing TableColumnType.Text columns.
 *
 * Return values:
 * - `{ isAllowed: true }` — fully selectable
 * - `{ isAllowed: true, disableReason }` — visible but grayed out with tooltip
 * - `{ isAllowed: false }` — hidden entirely (e.g. column code not found in schema)
 */
export const checkIsTextInput: ValidatingFunc = (question, valueListCode) => {
  if (question.valueType === UtrValueType.Text) {
    return { isAllowed: true };
  }

  if (question.valueType !== UtrValueType.Table) {
    return { isAllowed: true, disableReason: 'Not supported' };
  }

  if (!isSingleRowTableType(question)) {
    return { isAllowed: true, disableReason: 'Multi row table not supported' };
  }

  if (!valueListCode) {
    return { isAllowed: true };
  }

  const column = question.valueValidation?.table?.columns?.find((c) => c.code === valueListCode);
  if (!column) {
    return { isAllowed: false };
  }

  const isAllowed = column.type === TableColumnType.Text;
  return { isAllowed: true, disableReason: isAllowed ? undefined : 'Not supported' };
};
